import { useEffect, useMemo, useState } from "react"
import { useEquipments } from "../hooks/useEquipments"
import { useEquipmentIssues } from "../hooks/useEquipmentIssues"
import type { LinkedIssue } from "../lib/issueLink"
import {Search, X, ChevronRight, ArrowLeft} from 'lucide-react'

interface IssueLinkPickerModalProps {
  onClose: () => void
  onSelect: (issue: LinkedIssue) => void
  existingIssueIds?: string[]
}

const STATUS_LABEL: Record<string, string> = {
  issued: "발생",
  checking: "확인중",
  resolved: "조치완료",
  unavailable: "조치불가",
}

export function IssueLinkPickerModal({ onClose, onSelect, existingIssueIds = [] }: IssueLinkPickerModalProps) {
  const { equipments, fetchEquipments } = useEquipments()
  const { issues, loading: issuesLoading, fetchIssues } = useEquipmentIssues()

  const [step, setStep] = useState<"equipment" | "issue">("equipment")
  const [selectedEquipment, setSelectedEquipment] = useState<{ id: string; name: string } | null>(null)
  const [search, setSearch] = useState("")

  useEffect(() => {
    fetchEquipments()
  }, [fetchEquipments])

  // ESC 닫기
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose()
    }
    document.addEventListener("keydown", onKey)
    return () => document.removeEventListener("keydown", onKey)
  }, [onClose])

  const filteredEquipments = useMemo(() => {
    const kw = search.trim().toLowerCase()
    if (!kw) return equipments
    return equipments.filter((eq) =>
      (eq.productName || "").toLowerCase().includes(kw) ||
      (eq.customerMgmtNumber || "").toLowerCase().includes(kw)
    )
  }, [equipments, search])

  const handleEquipmentClick = (id: string, name: string) => {
    setSelectedEquipment({ id, name })
    setStep("issue")
    fetchIssues(id)
  }

  const handleIssueClick = (issueId: string, title: string, unitLabel?: string) => {
    if (!selectedEquipment) return
    onSelect({
      equipmentId: selectedEquipment.id,
      issueId,
      equipmentName: selectedEquipment.name,
      issueTitle: title,
      unitLabel,
    })
  }

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="bg-white rounded-xl shadow-2xl w-full max-w-lg max-h-[80vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* 헤더 */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200">
          <div className="flex items-center gap-2">
            {step === "issue" && (
              <button
                onClick={() => {
                  setStep("equipment")
                  setSelectedEquipment(null)
                }}
                className="p-1 rounded hover:bg-gray-100 text-gray-500"
                aria-label="뒤로"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
            )}
            <h3 className="text-lg font-bold text-gray-900">
              {step === "equipment" ? "장비 선택" : `이슈 선택 · ${selectedEquipment?.name ?? ""}`}
            </h3>
          </div>
          <button onClick={onClose} className="p-1 rounded hover:bg-gray-100 text-gray-500" aria-label="닫기">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* 본문 */}
        <div className="flex-1 overflow-y-auto p-4">
          {step === "equipment" ? (
            <>
              <div className="relative mb-3">
                <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="장비명 / 관리번호 검색"
                  className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-400 focus:border-orange-400 outline-none text-sm"
                />
              </div>
              {filteredEquipments.length === 0 ? (
                <p className="text-center text-gray-400 py-8 text-sm">장비가 없습니다.</p>
              ) : (
                <ul className="space-y-1">
                  {filteredEquipments.map((eq) => (
                    <li key={eq._id}>
                      <button
                        onClick={() => handleEquipmentClick(eq._id, eq.productName)}
                        className="w-full flex items-center justify-between px-3 py-2.5 rounded-lg hover:bg-orange-50 text-left transition-colors"
                      >
                        <div className="min-w-0">
                          <p className="font-medium text-gray-900 truncate">{eq.productName}</p>
                          {eq.customerMgmtNumber && (
                            <p className="text-xs text-gray-500 truncate">{eq.customerMgmtNumber}</p>
                          )}
                        </div>
                        <ChevronRight className="w-4 h-4 text-gray-400 shrink-0" />
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </>
          ) : (
            <>
              {issuesLoading ? (
                <p className="text-center text-gray-400 py-8 text-sm">이슈를 불러오는 중...</p>
              ) : issues.length === 0 ? (
                <p className="text-center text-gray-400 py-8 text-sm">등록된 이슈가 없습니다.</p>
              ) : (
                <ul className="space-y-1">
                  {issues.map((issue) => {
                    const already = existingIssueIds.includes(issue._id)
                    const label = issue.title?.trim() || issue.issueContent?.slice(0, 40) || "제목없음"
                    return (
                      <li key={issue._id}>
                        <button
                          disabled={already}
                          onClick={() => handleIssueClick(issue._id, issue.title?.trim() || label, issue.unitLabel)}
                          className={`w-full flex items-center justify-between px-3 py-2.5 rounded-lg text-left transition-colors ${
                            already ? "opacity-50 cursor-not-allowed" : "hover:bg-orange-50"
                          }`}
                        >
                          <div className="min-w-0">
                            <p className="font-medium text-gray-900 truncate">{label}</p>
                            <div className="flex items-center gap-2 mt-0.5">
                              <span className="text-xs px-1.5 py-0.5 rounded bg-gray-100 text-gray-600">
                                {STATUS_LABEL[issue.status] || issue.status}
                              </span>
                              {issue.unitLabel && (
                                <span className="text-xs text-gray-500 truncate">{issue.unitLabel}</span>
                              )}
                            </div>
                          </div>
                          {already ? (
                            <span className="text-xs text-orange-600 shrink-0">추가됨</span>
                          ) : (
                            <ChevronRight className="w-4 h-4 text-gray-400 shrink-0" />
                          )}
                        </button>
                      </li>
                    )
                  })}
                </ul>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  )
}
